import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import SaleCountdown from "@/components/SaleCountdown";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import CatalogRequestModal from "@/components/CatalogRequestModal";
import BullMapSection from "@/components/BullMapSection";
import ConsignorCarousel from "@/components/ConsignorCarousel";

export default function HomePage() {
  const [settings, setSettings] = useState(null);
  const [sales, setSales] = useState([]);
  const [consignors, setConsignors] = useState([]);
  const [videos, setVideos] = useState([]);
  const [buyerInfo, setBuyerInfo] = useState(null);
  const [sires, setSires] = useState([]);
  const [catalogOpen, setCatalogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [s, sa, c, v, b, sr] = await Promise.all([
          api.get("/site-settings"),
          api.get("/sales"),
          api.get("/consignors"),
          api.get("/videos"),
          api.get("/buyer-info"),
          api.get("/featured-sires"),
        ]);
        setSettings(s.data);
        setSales(sa.data);
        setConsignors(c.data);
        setVideos(v.data);
        setBuyerInfo(b.data);
        setSires(sr.data);
      } catch (e) {
        if (process.env.NODE_ENV === "development") console.error(e);
      }
    })();
  }, []);

  const annual = sales.find((s) => s.slug === "annual");
  const legacy = sales.find((s) => s.slug === "legacy");

  return (
    <div data-testid="home-page" className="min-h-screen" style={{ background: "var(--bg-primary)" }}>
      <SiteHeader />

      {/* HERO */}
      <section data-testid="hero-section" className="relative min-h-screen w-full flex items-end overflow-hidden pt-32 md:pt-36">
        <div className="absolute inset-0">
          <img
            src={settings?.hero_image_url || "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/owuwhzm5_IMG_1370.jpeg"}
            alt="Montana ranch cattle with snowy mountain backdrop"
            className="w-full h-full object-cover"
            style={{ objectPosition: "center 55%" }}
          />
        </div>
        <div className="absolute inset-0 hero-overlay" />
        <div className="grain absolute inset-0" />

        {/* layered mountain silhouettes — anchors the Montana feel */}
        <svg
          aria-hidden
          viewBox="0 0 1440 300"
          preserveAspectRatio="none"
          className="absolute bottom-0 left-0 w-full pointer-events-none"
          style={{ height: "38vh", minHeight: 220, opacity: 0.55, color: "var(--brass-deep)" }}
        >
          <path
            d="M0,300 L0,220 L120,160 L220,200 L320,120 L440,180 L560,140 L680,210 L800,110 L920,170 L1040,150 L1160,200 L1280,130 L1380,190 L1440,165 L1440,300 Z"
            fill="#0E2C46"
            opacity="0.7"
          />
          <path
            d="M0,300 L0,250 L100,210 L240,240 L360,205 L480,245 L600,215 L740,250 L860,210 L980,245 L1120,220 L1260,245 L1400,225 L1440,235 L1440,300 Z"
            fill="#13345A"
            opacity="0.9"
          />
        </svg>

        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-10 md:pb-14">
          <div className="font-script text-3xl md:text-4xl reveal" style={{ color: "#E8D7A8" }}>
            Est. 2009
          </div>
          <div className="eyebrow mt-1 reveal reveal-delay-1" style={{ color: "#E8D7A8", letterSpacing: "0.4em" }}>
            Montana ★ Bulls That Just Work
          </div>
          <h1
            data-testid="hero-title"
            className="font-display text-[3.5rem] sm:text-7xl md:text-8xl lg:text-[9.5rem] mt-4 reveal reveal-delay-2"
            style={{ color: "#FFFFFF", textShadow: "0 4px 24px rgba(14,44,70,0.55)" }}
          >
            {settings?.hero_title || "Bulls of the Big Sky"}
          </h1>
          <p
            className="font-italic-serif text-2xl md:text-4xl mt-4 reveal reveal-delay-3"
            style={{ color: "rgba(255,255,255,0.92)" }}
          >
            {settings?.hero_subtitle || "Proven Simmental, SimAngus & Angus Genetics"}
          </p>
          <p
            className="mt-6 text-base md:text-lg max-w-2xl reveal reveal-delay-3"
            style={{ color: "rgba(255,255,255,0.85)" }}
          >
            {settings?.hero_tagline}
          </p>

          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4 reveal reveal-delay-4">
            <Link to="/sale/legacy" data-testid="hero-cta-legacy" className="btn-primary" style={{ background: "var(--surface)", color: "var(--brass-deep)", borderColor: "var(--surface)" }}>
              View Legacy Sale →
            </Link>
            <Link to="/sale/annual" data-testid="hero-cta-annual" className="btn-ghost-light">
              2027 Annual Sale →
            </Link>
            <button
              type="button"
              data-testid="hero-cta-catalog"
              onClick={() => setCatalogOpen(true)}
              className="btn-primary"
              style={{ background: "var(--hay)", color: "#0E2C46", borderColor: "var(--hay)" }}
            >
              Request Catalog →
            </button>
          </div>

          <Link to="/sale/legacy" className="block" data-testid="hero-countdown-link">
            <SaleCountdown
              date="2026-12-05T13:00:00-07:00"
              eyebrow="Legacy of the Big Sky"
              sublabel="Dec 5  •  Billings, MT"
            />
          </Link>
        </div>
      </section>

      {/* BRAND INTRO */}
      <section data-testid="brand-intro" className="relative py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-3">
            <div className="eyebrow">About</div>
          </div>
          <div className="md:col-span-9">
            <p className="font-display text-2xl md:text-5xl leading-[1.15] md:leading-[1.1]" style={{ color: "var(--text-primary)" }}>
              {settings?.brand_intro}
            </p>
            <p className="mt-8 text-base md:text-lg max-w-3xl" style={{ color: "var(--text-secondary)" }}>
              Eight programs, one catalog. Every bull is developed on a forage-based ration —
              Annual Sale bulls at Veltkamp&apos;s in Manhattan, Legacy Sale bulls at Fauth Ranch in Lavina —
              sound, athletic and ready to work. We&apos;ve stood behind them since 2009.
            </p>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — moved to the bottom, just above the catalog CTA */}

      {/* SALE EVENTS */}
      <section data-testid="sales-section" className="relative">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12">
            <div className="md:col-span-3"><div className="eyebrow">The Sales</div></div>
            <div className="md:col-span-9">
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
                Two sales.<br />One standard.
              </h2>
            </div>
          </div>
        </div>

        {/* Legacy — next upcoming sale */}
        {legacy && (
          <SaleBlock
            sale={legacy}
            badge="Next Sale  •  December 5, 2026"
            primary
            data-testid="sale-block-legacy"
          />
        )}
        {/* Annual 2027 — save the date */}
        {annual && (
          <SaleBlock
            sale={annual}
            badge="2027 Annual  •  200+ Bulls"
            primary={false}
            reverse
            data-testid="sale-block-annual"
          />
        )}
      </section>

      {/* CONSIGNORS */}
      <section data-testid="consignors-section" className="relative py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4">Meet the Consignors</div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]">
                Eight ranches.<br />One catalog.
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base md:text-lg" style={{ color: "var(--text-secondary)" }}>
                One consistent set of bulls — eight different breeding perspectives behind it.
              </p>
              <Link to="/consignors" data-testid="consignors-view-all" className="link-brass mt-6 inline-block text-sm">
                View all consignors →
              </Link>
            </div>
          </div>

          <ConsignorCarousel consignors={consignors} />
        </div>
      </section>

      {/* BULL DELIVERY MAP */}
      <BullMapSection />

      {/* BLOODLINES BLURB — current AI sires used in upcoming sales */}
      <section data-testid="bloodlines-section" className="section-deep relative py-24 md:py-32 overflow-hidden">
        <div className="mountain-watermark" style={{ opacity: 0.10 }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 mb-12 items-end">
            <div className="md:col-span-7">
              <div className="eyebrow mb-4" style={{ color: "var(--sky-pale)" }}>Featured Bloodlines  •  2027 Lineup</div>
              <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ color: "var(--surface)" }}>
                Sire-<br />Groups
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-base md:text-lg" style={{ color: "rgba(255,255,255,0.8)" }}>
                Proven, high-accuracy sires alongside the freshest new bloodlines —
                sons sell in every sale.
              </p>
              {/* "View all featured sires" link hidden along with Semen Directory page — restore when page is unhidden */}
            </div>
          </div>

          {sires.length > 0 && (
            <div className="sire-marquee" data-testid="sire-marquee">
              <div className="sire-marquee-track">
                {[...sires, ...sires].map((s, i) => (
                  <Link
                    key={`${s.id}-${i}`}
                    to="/sires"
                    data-testid={`bloodline-card-${i}`}
                    className="frame-image group relative block flex-shrink-0 w-[260px] md:w-[340px]"
                    style={{ aspectRatio: "4 / 3" }}
                  >
                  <img src={s.photo_url} alt={s.name} className="w-full h-full object-cover" style={{ objectPosition: "center top" }} />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(7,11,20,0.95) 0%, rgba(7,11,20,0.3) 50%, transparent 100%)" }} />
                  <div className="absolute inset-x-0 bottom-0 p-4 md:p-6">
                    <div className="eyebrow text-[10px] mb-1" style={{ color: "var(--brass-bright)" }}>
                      {s.breed || "SimGenetics"}
                    </div>
                    <div className="font-display text-lg md:text-2xl leading-tight">
                      {s.nickname || s.name}
                    </div>
                    {s.nickname && (
                      <div className="text-[11px] mt-1" style={{ color: "var(--text-muted)" }}>
                        {s.name}
                      </div>
                    )}
                  </div>
                </Link>
              ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* TESTIMONIALS — removed for now per user request (re-enable once real testimonials are collected) */}

      {/* CATALOG REQUEST CTA */}
      <section data-testid="catalog-cta-section" className="section-deep relative py-24 md:py-32 overflow-hidden">
        <div className="mountain-watermark" style={{ opacity: 0.12 }} />
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-7">
            <div className="eyebrow mb-4" style={{ color: "var(--sky-pale)" }}>2026 Sale Book</div>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ color: "var(--surface)" }}>
              Get the<br />catalog in hand.
            </h2>
            <p className="mt-6 max-w-2xl text-base md:text-lg" style={{ color: "rgba(255,255,255,0.82)" }}>
              Drop your name and number — we&apos;ll mail you the printed sale book and send sale-day text
              updates so you don&apos;t miss a lot.
            </p>
          </div>
          <div className="md:col-span-5 flex md:justify-end">
            <button
              type="button"
              data-testid="catalog-cta-btn"
              onClick={() => setCatalogOpen(true)}
              className="btn-primary"
              style={{ background: "var(--surface)", color: "var(--brass-deep)", borderColor: "var(--surface)" }}
            >
              Request a Catalog →
            </button>
          </div>
        </div>
      </section>

      <SiteFooter settings={settings} />
      <CatalogRequestModal open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </div>
  );
}

function SaleBlock({ sale, badge, primary, reverse }) {
  return (
    <div className="relative py-16 md:py-24" data-testid={`sale-block-${sale.slug}`}>
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center ${reverse ? "md:[direction:rtl]" : ""}`}>
          <div className="md:col-span-7 md:[direction:ltr]">
            <div className="frame-image relative" style={{ aspectRatio: "4 / 3" }}>
              <img src={sale.hero_image_url} alt={sale.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(14,44,70,0.45), rgba(14,44,70,0.05))" }} />
              <div className="absolute top-6 left-6">
                <div className="px-4 py-2 backdrop-blur-md eyebrow text-[11px]"
                  style={{ background: "rgba(14,44,70,0.85)", border: "1px solid rgba(191,212,224,0.5)", color: "var(--surface)" }}>
                  {badge}
                </div>
              </div>
            </div>
          </div>
          <div className="md:col-span-5 md:[direction:ltr]">
            <div className="eyebrow mb-4">{sale.tagline}</div>
            <h3 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95]" style={{ color: "var(--brass-deep)" }}>
              {sale.name}
            </h3>
            <div className="mt-6 font-italic-serif text-2xl md:text-3xl" style={{ color: "var(--brass)" }}>
              {sale.date_label}
            </div>
            <div className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{sale.location}</div>
            <p className="mt-6 text-base md:text-lg leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              {sale.description}
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link
                to={`/sale/${sale.slug}`}
                data-testid={`sale-${sale.slug}-details`}
                className={primary ? "btn-primary" : "btn-ghost"}
              >
                View {sale.name.replace(/^\d+(st|nd|rd|th)\s+/i, "")} →
              </Link>
              {sale.slug === "legacy" && (
                <Link
                  to="/buyer-info/legacy"
                  data-testid="sale-legacy-terms-btn"
                  className="btn-ghost"
                >
                  Buyer Info & Sale Terms
                </Link>
              )}
              {sale.catalog_url && (
                <a
                  href={sale.catalog_url}
                  target="_blank" rel="noopener noreferrer"
                  data-testid={`sale-${sale.slug}-catalog`}
                  className="btn-ghost"
                >
                  Catalog (PDF)
                </a>
              )}
            </div>
            {sale.slug === "legacy" && (
              <a
                href="/images/legacy-sale-flyer.jpg"
                target="_blank" rel="noopener noreferrer"
                data-testid="legacy-flyer-link"
                className="mt-6 inline-flex items-center gap-4 group"
              >
                <img
                  src="/images/legacy-sale-flyer.jpg"
                  alt="Official Legacy Sale flyer"
                  loading="lazy"
                  className="w-20 md:w-24 h-auto transition-transform duration-300 group-hover:scale-[1.04]"
                  style={{ border: "1px solid var(--border-subtle)", boxShadow: "0 4px 14px rgba(14,44,70,0.18)" }}
                />
                <span className="link-brass text-sm">View the official<br />sale flyer →</span>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function VideoCard({ v }) {
  const isYouTube = v.youtube_id && !v.youtube_id.startsWith("http");
  const isUrl = v.youtube_id && v.youtube_id.startsWith("http");
  return (
    <div className="border" style={{ borderColor: "var(--border-subtle)" }}>
      <div className="relative bg-black" style={{ aspectRatio: "16 / 9" }}>
        {isYouTube && (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${v.youtube_id}`}
            title={v.title}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        )}
        {!isYouTube && v.thumbnail_url && (
          <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
        )}
      </div>
      <div className="p-5">
        <div className="font-display text-xl">{v.title}</div>
        {v.description && <p className="text-sm mt-2" style={{ color: "var(--text-secondary)" }}>{v.description}</p>}
        {isUrl && (
          <a href={v.youtube_id} target="_blank" rel="noopener noreferrer" className="link-brass text-sm mt-3 inline-block">
            Watch →
          </a>
        )}
      </div>
    </div>
  );
}
